import React from 'react';

// Performance monitoring utilities
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number> = new Map();
  private observers: PerformanceObserver[] = [];

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Track component render time
  trackRender(componentName: string, startTime: number) {
    const renderTime = performance.now() - startTime;
    this.metrics.set(`${componentName}_render`, renderTime);
    
    if (renderTime > 16) { // More than one frame
      console.warn(`🐌 Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`);
    }
  }

  // Track API call performance
  trackApiCall(apiName: string, startTime: number) {
    const duration = performance.now() - startTime;
    this.metrics.set(`${apiName}_api`, duration);
    
    if (duration > 1000) { // More than 1 second
      console.warn(`🐌 Slow API call: ${apiName} took ${duration.toFixed(2)}ms`);
    }
  }

  // Track bundle loading
  trackBundleLoad() {
    if ('performance' in window && 'getEntriesByType' in performance) {
      const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      
      if (entries.length > 0) {
        const nav = entries[0];
        const metrics = {
          domContentLoaded: nav.domContentLoadedEventEnd - nav.domContentLoadedEventStart,
          loadComplete: nav.loadEventEnd - nav.loadEventStart,
          totalLoadTime: nav.loadEventEnd - nav.fetchStart,
        };
        
        // Store for analytics
        this.metrics.set('dom_content_loaded', metrics.domContentLoaded);
        this.metrics.set('load_complete', metrics.loadComplete);
        this.metrics.set('total_load_time', metrics.totalLoadTime);
      }
    }
  }

  // Track memory usage
  trackMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const memoryUsage = {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024), // MB
      };
      
      if (memoryUsage.used > 100) { // More than 100MB
        console.warn('⚠️ High memory usage detected:', memoryUsage.used + 'MB');
      }
      
      return memoryUsage;
    }
    return null;
  }

  // Get all metrics
  getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  // Clear metrics
  clearMetrics() {
    this.metrics.clear();
  }

  // Initialize monitoring (alias for startMonitoring)
  init() {
    this.startMonitoring();
  }

  // Get optimization suggestions
  getSuggestions() {
    const suggestions = [];
    const memory = this.trackMemoryUsage();
    
    if (memory && memory.used > 100) {
      suggestions.push({
        type: 'memory',
        message: 'High memory usage detected. Consider optimizing images and reducing component re-renders.',
        priority: 'high'
      });
    }

    const metrics = this.getMetrics();
    if (metrics.dom_content_loaded > 3000) {
      suggestions.push({
        type: 'performance',
        message: 'Slow DOM content loaded. Consider code splitting and lazy loading.',
        priority: 'medium'
      });
    }

    return suggestions;
  }

  // Generate performance report
  generateReport() {
    const metrics = this.getMetrics();
    const memory = this.trackMemoryUsage();
    const suggestions = this.getSuggestions();
    
    return {
      timestamp: new Date().toISOString(),
      metrics,
      memory,
      suggestions,
      bundleAnalysis: analyzeBundleSize()
    };
  }

  // Start monitoring
  startMonitoring() {
    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            console.warn(`🐌 Long task detected: ${entry.duration.toFixed(2)}ms`);
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
        this.observers.push(longTaskObserver);
      } catch (e) {
        // Long task API not supported
      }
    }

    // Monitor memory usage periodically
    setInterval(() => {
      this.trackMemoryUsage();
    }, 30000); // Every 30 seconds

    // Track bundle load on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.trackBundleLoad();
      });
    } else {
      this.trackBundleLoad();
    }
  }

  // Stop monitoring
  stopMonitoring() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// React hook for performance tracking
export const usePerformanceTracking = (componentName: string) => {
  const monitor = PerformanceMonitor.getInstance();
  
  const trackRender = () => {
    const startTime = performance.now();
    return () => monitor.trackRender(componentName, startTime);
  };

  const trackApiCall = (apiName: string) => {
    const startTime = performance.now();
    return () => monitor.trackApiCall(apiName, startTime);
  };

  return { trackRender, trackApiCall };
};

// Higher-order component for performance tracking
export const withPerformanceTracking = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) => {
  return (props: P) => {
    const { trackRender } = usePerformanceTracking(componentName);
    const endTracking = trackRender();
    
    React.useEffect(() => {
      endTracking();
    });

    return React.createElement(WrappedComponent, props);
  };
};

// Utility to measure function execution time
export const measureExecutionTime = <T extends (...args: any[]) => any>(
  fn: T,
  name: string
): T => {
  return ((...args: Parameters<T>) => {
    const start = performance.now();
    const result = fn(...args);
    const end = performance.now();
    
    console.log(`⏱️ ${name} executed in ${(end - start).toFixed(2)}ms`);
    
    return result;
  }) as T;
};

// Bundle size analyzer
export const analyzeBundleSize = () => {
  if ('performance' in window && 'getEntriesByType' in performance) {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    
    const jsFiles = resources.filter(resource => 
      resource.name.includes('.js') && resource.name.includes('static')
    );
    
    const totalSize = jsFiles.reduce((total, file) => total + file.transferSize, 0);
    const totalSizeKB = Math.round(totalSize / 1024);
    
    jsFiles.forEach(file => {
      const sizeKB = Math.round(file.transferSize / 1024);
      const fileName = file.name.split('/').pop();
      });
    
    return {
      totalSizeKB,
      files: jsFiles.map(file => ({
        name: file.name.split('/').pop(),
        sizeKB: Math.round(file.transferSize / 1024)
      }))
    };
  }
  
  return null;
};

export const performanceMonitor = PerformanceMonitor.getInstance();
export default PerformanceMonitor;